import axios from 'axios';
import { paymentActionTypes as actions } from '~client/constants/actionTypes';
import { communicationStatus } from '~client/constants';
import { fetch, updateCommunicationStatus, handleFetchCatch } from '~actions/shared';
import * as queries from '~settings/queries';
import endpoints from '~settings/endpoints';
import { formatSimpleParams } from '~helpers';
import { message } from 'antd';
import i18n from '~settings/i18n';

export const setList = (payload, filterPaymentProcess) => ({
  type: actions.SET_LIST,
  payload,
  filterPaymentProcess,
});

export const getList =
  (params, filterPaymentProcess = false) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.GET_LIST_CS,
      params: queries.getPaymentList(
        formatSimpleParams(
          {
            pageNumber: 1,
            pageSize: 12,
            sortBy: 'UPDATED_AT',
            sortDirection: 'DESCENDING',
          },
          params,
        ),
      ),
      dispatch,
      successAction: payload => setList(payload, filterPaymentProcess),
    });
  };

export const setSetup = params => ({
  type: actions.SET_SETUP,
  propertyId: params.id,
  paymentProcess: {
    method: params.paymentProcess || null,
    connectedAccountId: params.connectedAccountId || null,
  },
  deposits: params.paymentDepositRules.edges || [],
  termsAndConditions: params.paymentTermsAndConditions.edges || [],
  paymentsEnabled: params.paymentsEnabled || false,
});

export const setSelectedPropertiesIds = selectedPropertiesIds => ({
  type: actions.SET_SELECTED_PROPERTY_IDS,
  selectedPropertiesIds,
});

export const setStripeClientId = clientId => ({
  type: actions.SET_STRIPE_CLIENT_ID,
  clientId,
});

export const getStripeClientId = currency => dispatch => {
  dispatch(
    updateCommunicationStatus({
      actionType: actions.GET_STRIPE_CLIENT_ID_CS,
      status: communicationStatus.FETCHING,
    }),
  );
  axios
    .get(endpoints.getStripeClientId.url(currency))
    .then(response => {
      if (typeof response.data === 'object' && response.data.client_id) {
        // Valid client_id in response:
        dispatch(
          updateCommunicationStatus({
            actionType: actions.GET_STRIPE_CLIENT_ID_CS,
            status: communicationStatus.IDLE,
          }),
        );
        dispatch(setStripeClientId(response.data.client_id));
      } else {
        // Invalid client_id in response:
        dispatch(
          updateCommunicationStatus({
            actionType: actions.GET_STRIPE_CLIENT_ID_CS,
            status: communicationStatus.ERROR,
            error: response.data,
          }),
        );
      }
    })
    .catch(error => {
      handleFetchCatch({
        actionType: actions.GET_STRIPE_CLIENT_ID_CS,
        dispatch,
        error,
      });
    });
};

export const setConnectedAccountId = payload => ({
  type: actions.SET_CONNECTED_ACCOUNT_ID,
  connectedAccountId: payload.createConnectedAccount.connectedAccountId,
});

export const createConnectedAccount = (authCode, currency) => dispatch => {
  fetch({
    endpoint: endpoints.graphql.url(),
    communicationType: actions.CREATE_CONNECTED_ACCOUNT_CS,
    params: queries.createConnectedAccount(
      formatSimpleParams(
        {
          authCode: null,
          currency: null,
        },
        { authCode, currency },
      ),
    ),
    dispatch,
    successAction: setConnectedAccountId,
  });
};

export const updatePaymentDepositRules = paymentDepositRules => ({
  type: actions.UPDATE_PAYMENT_DEPOSIT_RULES,
  paymentDepositRules,
});

export const updatePaymentFirstInstalment = payload => ({
  type: actions.UPDATE_PAYMENT_FIRST_INSTALMENT,
  payload,
});

export const skipPaymentFirstInstalment = () => ({
  type: actions.SKIP_PAYMENT_FIRST_INSTALMENT,
});

export const updateTermsAndConditions = termsAndConditions => ({
  type: actions.UPDATE_TERMS_AND_CONDITIONS,
  termsAndConditions,
});

export const updatePaymentProcess = params => ({
  type: actions.UPDATE_PAYMENT_PROCESS,
  method: params.method,
});

export const updatePaymentEnabled = paymentsEnabled => ({
  type: actions.UPDATE_PAYMENT_ENABLED,
  paymentsEnabled,
});

export const resetPayment = () => ({
  type: actions.RESET_PAYMENT,
});

export const setCreateLongtailPaymentPlan = payload => ({
  type: actions.SET_LONG_TAIL_PAYMENT_PLAN,
  payload: payload.createPaymentPlan.paymentPlan,
});

export const setUpdateLongtailPaymentPlan = payload => ({
  type: actions.SET_LONG_TAIL_PAYMENT_PLAN,
  payload: payload.updatePaymentPlan.paymentPlan,
});

export const setGetLongtailPaymentPlan = payload => ({
  type: actions.SET_LONG_TAIL_PAYMENT_PLAN,
  payload: payload.getPaymentPlan.paymentPlan,
});

export const resetLongtailPaymentPlan = () => ({
  type: actions.RESET_LONG_TAIL_PAYMENT_PLAN,
});

export const createLongtailPaymentPlan =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.CREATE_LONG_TAIL_PAYMENT_PLAN_CS,
      params: queries.createLongtailPaymentPlan(
        formatSimpleParams(
          {
            currency: null,
            description: null,
            endDate: null,
            name: null,
            properties: [],
            startDate: null,
            category: null,
            paymentOnline: null,
          },
          params,
        ),
      ),
      dispatch,
      successAction: setCreateLongtailPaymentPlan,
      onSuccess: res => {
        onSuccess(res);
      },
      onError: (e, code) => {
        if (
          (e &&
            e.length > 0 &&
            e[0].message &&
            e[0].message.indexOf('PROPERTY_ALREADY_BIND_PAYMENT_PLAN') !== -1) ||
          (code && code.indexOf('PROPERTY_ALREADY_BIND_PAYMENT_PLAN') !== -1)
        ) {
          message.error(i18n.t('landlord.payment.long_tail.payment_plan.create.property_bind_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
      },
    });
  };

export const updateLongtailPaymentPlan =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.UPDATE_LONG_TAIL_PAYMENT_PLAN_CS,
      params: queries.updateLongtailPaymentPlan(
        formatSimpleParams(
          {
            id: null,
            currency: null,
            description: null,
            endDate: null,
            name: null,
            properties: [],
            startDate: null,
          },
          params,
        ),
      ),
      dispatch,
      successAction: setUpdateLongtailPaymentPlan,
      onSuccess: res => {
        onSuccess(res);
      },
      onError: () => {
        message.error(i18n.t('landlord.error.default'));
      },
    });
  };

export const getLongtailPaymentPlan =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.GET_LONG_TAIL_PAYMENT_PLAN_CS,
      params: queries.getLongtailPaymentPlan(params),
      dispatch,
      successAction: setGetLongtailPaymentPlan,
      onSuccess: res => {
        onSuccess(res);
      },
    });
  };

export const createLongtailOrderInstalment =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.CREATE_LONG_TAIL_INSTALMENT_PLAN_CS,
      params: queries.createLongtailOrderInstalment(
        formatSimpleParams(
          {
            fromTenancy: null,
            fullPayment: false,
            payMonthly: false,
            payQuarterly: false,
            name: null,
            paymentPlanId: null,
            requireGuarantor: null,
            tenancyUnit: 'WEEKS',
            toTenancy: null,
          },
          params,
        ),
      ),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.instalment_plan.create.toast'));
      },
      onError: () => {
        message.error(i18n.t('landlord.error.default'));
      },
    });
  };

export const updateLongtailOrderInstalment =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.UPDATE_LONG_TAIL_INSTALMENT_PLAN_CS,
      params: queries.updateLongtailOrderInstalment(
        formatSimpleParams(
          {
            fromTenancy: null,
            fullPayment: false,
            payMonthly: false,
            payQuarterly: false,
            name: null,
            requireGuarantor: null,
            tenancyUnit: 'WEEKS',
            toTenancy: null,
            id: null,
          },
          params,
        ),
      ),
      dispatch,
      onSuccess: () => {
        onSuccess();
        message.success(i18n.t('landlord.payment.long_tail.instalment_plan.update.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'ORDER_INSTALMENT_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.instalment_plan.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
      },
    });
  };

export const deleteLongtailOrderInstalment =
  (id, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.DELETE_LONG_TAIL_INSTALMENT_PLAN_CS,
      params: queries.deleteLongtailOrderInstalment(id),
      dispatch,
      onSuccess: () => {
        onSuccess();
        message.success(i18n.t('landlord.payment.long_tail.instalment_plan.delete.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'ORDER_INSTALMENT_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.instalment_plan.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
      },
    });
  };

export const createOrderDeposit =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.CREATE_ORDER_DEPOSIT_CS,
      params: queries.createOrderDeposit(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.deposit.create.toast'));
      },
      onError: () => {
        message.error(i18n.t('landlord.error.default'));
      },
    });
  };

export const updateOrderDeposit =
  (params, onSuccess = () => {}, onError = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.UPDATE_ORDER_DEPOSIT_CS,
      params: queries.updateOrderDeposit(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.deposit.update.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'ORDER_DEPOSIT_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.deposit.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }

        onError();
      },
    });
  };

export const deleteOrderDeposit =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.DELETE_ORDER_DEPOSIT_CS,
      params: queries.deleteOrderDeposit(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.deposit.delete.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'ORDER_DEPOSIT_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.deposit.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
      },
    });
  };

export const createOrderFee =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.CREATE_ORDER_FEE_CS,
      params: queries.createOrderFee(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.fee.create.toast'));
      },
      onError: () => {
        message.error(i18n.t('landlord.error.default'));
      },
    });
  };

export const updateOrderFee =
  (params, onSuccess = () => {}, onError = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.UPDATE_ORDER_FEE_CS,
      params: queries.updateOrderFee(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.fee.update.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'ORDER_FEE_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.fee.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
        onError();
      },
    });
  };

export const deleteOrderFee =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.DELETE_ORDER_FEE_CS,
      params: queries.deleteOrderFee(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.fee.delete.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'ORDER_FEE_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.fee.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
      },
    });
  };

export const setLongtailPaymentPlans = payload => ({
  type: actions.SET_LONGTAIL_PAYMENT_PLANS,
  payload: payload.paymentPlans,
});

export const getLongtailPaymentPlans =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.GET_LONGTAIL_PAYMENT_PLANS_CS,
      params: queries.getLongtailPaymentList(
        formatSimpleParams(
          {
            pageNumber: 1,
            pageSize: 12,
            sortBy: 'UPDATED_AT',
            sortDirection: 'DESCENDING',
          },
          params,
        ),
      ),
      dispatch,
      successAction: setLongtailPaymentPlans,
      onSuccess: res => {
        onSuccess(res);
      },
      onError: () => {
        message.error(i18n.t('landlord.error.default'));
      },
    });
  };

export const getLongtailPaymentPlansProperties =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.GET_LONGTAIL_PAYMENT_PLANS_CS,
      params: queries.getLongtailPaymentListProperties(
        formatSimpleParams(
          {
            pageNumber: 1,
            pageSize: 12,
            sortBy: 'UPDATED_AT',
            sortDirection: 'DESCENDING',
          },
          params,
        ),
      ),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
      },
    });
  };

export const deleteLongtailPayment =
  (params, onSuccess = () => {}) =>
  dispatch => {
    fetch({
      endpoint: endpoints.graphql.url(),
      communicationType: actions.DELETE_LONG_TAIL_PAYMENT_PLAN_CS,
      params: queries.deleteLongtailPayment(params),
      dispatch,
      onSuccess: res => {
        onSuccess(res);
        message.success(i18n.t('landlord.payment.long_tail.payment_plan.delete.toast'));
      },
      onError: e => {
        if (e && e.length > 0 && e[0].message && e[0].message === 'PAYMENT_PLAN_NOT_FOUND') {
          message.error(i18n.t('landlord.payment.long_tail.payment_plan.delete.not_found_err'));
        } else {
          message.error(i18n.t('landlord.error.default'));
        }
      },
    });
  };
